import { useCallback, useEffect, useRef, useState } from "react";
import { investorsApi } from "@/features/investors/api/investors.api";
import { InvestorsDashboardData } from "@/features/investors/types/investors.types";
import { QuarterOption } from "@/features/investors/utils/quarters";

type State = {
    data: InvestorsDashboardData | null;
    loading: boolean;
    isFetching: boolean;
    error: string | null;
};

const INITIAL_STATE: State = { data: null, loading: true, isFetching: true, error: null };

export const useInvestorsDashboard = (quarter: QuarterOption) => {
    const [state, setState] = useState<State>(INITIAL_STATE);
    const cacheRef = useRef(new Map<string, InvestorsDashboardData>());
    const requestSeqRef = useRef(0);

    const load = useCallback(
        async (target: QuarterOption, options: { skipCache: boolean }) => {
            const requestId = ++requestSeqRef.current;
            const cached = options.skipCache ? undefined : cacheRef.current.get(target.id);

            setState({
                data: cached ?? null,
                loading: cached === undefined,
                isFetching: true,
                error: null,
            });

            try {
                const data = await investorsApi.fetchInvestorsDashboard(target.start, target.end);
                if (requestId !== requestSeqRef.current) return;
                cacheRef.current.set(target.id, data);
                setState({ data, loading: false, isFetching: false, error: null });
            } catch (error) {
                if (requestId !== requestSeqRef.current) return;
                const message = error instanceof Error ? error.message : "Request failed";
                setState({ data: cached ?? null, loading: false, isFetching: false, error: message });
            }
        },
        []
    );

    useEffect(() => {
        void load(quarter, { skipCache: false });
    }, [quarter, load]);

    const refresh = useCallback(() => {
        void load(quarter, { skipCache: true });
    }, [quarter, load]);

    return { ...state, refresh };
};
